import React from "react";

/**
 * PlaceholderImage
 * - If `src` prop is provided, renders a real <img> with the same aspect-ratio wrapper.
 * - Otherwise falls back to the labeled placeholder block.
 *
 * This lets us drop in real assets one prop at a time without touching layout.
 */
const ratioMap = {
  hero: "aspect-[16/9] lg:aspect-[21/9]",
  wide: "aspect-[16/9]",
  banner: "aspect-[3/1]",
  square: "aspect-square",
  portrait: "aspect-[4/5]",
  landscape: "aspect-[16/10]",
  tall: "aspect-[3/4]",
  short: "aspect-[4/1]",
  fill: "h-full",
};

export default function PlaceholderImage({
  label = "Website Banner",
  ratio = "landscape",
  tone = "light",
  overlayTitle,
  overlaySubtitle,
  className = "",
  testId,
  children,
  src,
  alt,
  objectPosition = "center",
  loading = "lazy",
  objectFit = "cover",
}) {
  const aspect = ratioMap[ratio] || ratio;
  const dark = tone === "dark";

  // Real-image mode
  if (src) {
    return (
      <div
        data-testid={testId}
        data-placeholder-label={label}
        className={`relative overflow-hidden ${aspect} ${className}`}
        style={objectFit === "contain" ? { backgroundColor: "rgb(var(--secondary-background))" } : undefined}
      >
        <img
          src={src}
          alt={alt || label}
          loading={loading}
          className={`absolute inset-0 w-full h-full ${objectFit === "contain" ? "object-contain" : "object-cover"}`}
          style={{ objectPosition }}
        />
        {children && (
          <div className="absolute inset-0 flex items-center justify-center px-6">
            <div className="text-center w-full max-w-4xl mx-auto">{children}</div>
          </div>
        )}
      </div>
    );
  }

  // Placeholder mode
  return (
    <div
      data-testid={testId}
      data-placeholder-label={label}
      className={`mp-placeholder ${dark ? "mp-placeholder-dark" : ""} ${aspect} ${className}`}
    >
      <div
        className={`absolute top-4 left-4 z-10 flex items-center gap-2 px-2.5 py-1 border ${
          dark ? "border-white/15 bg-black/60 text-white/80" : "border-black/15 bg-white/80 text-black/70"
        }`}
      >
        <span className="inline-block w-1.5 h-1.5" style={{ background: "var(--mp-green)" }} />
        <span className="font-heading uppercase tracking-[0.24em] text-[10px]">
          {label}
        </span>
      </div>

      <div className="absolute inset-0 flex items-center justify-center px-6">
        {children ? (
          <div className="text-center max-w-md w-full">{children}</div>
        ) : (
          <div className="text-center max-w-md w-full">
            {overlayTitle && (
              <div className={`font-display text-4xl sm:text-5xl lg:text-6xl leading-[0.9] uppercase ${dark ? "text-white/15" : "text-black/15"}`}>
                {overlayTitle}
              </div>
            )}
            {overlaySubtitle && (
              <div className={`mt-3 font-heading uppercase tracking-[0.3em] text-[10px] ${dark ? "text-white/40" : "text-black/40"}`}>
                {overlaySubtitle}
              </div>
            )}
            {!overlayTitle && !overlaySubtitle && (
              <>
                <div className={`font-display text-3xl sm:text-4xl lg:text-5xl uppercase leading-[0.9] ${dark ? "text-white/15" : "text-black/15"}`}>
                  MUSCLEPHARM
                </div>
                <div className={`mt-2 font-heading uppercase tracking-[0.35em] text-[10px] ${dark ? "text-white/30" : "text-black/30"}`}>
                  image slot · swap final asset
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {["top-3 right-3 border-t border-r", "bottom-3 left-3 border-b border-l", "bottom-3 right-3 border-b border-r"].map((cls) => (
        <div key={cls} className={`absolute w-4 h-4 ${cls} ${dark ? "border-white/20" : "border-black/20"}`} />
      ))}
    </div>
  );
}
